var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Alert, Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography, } from "@mui/material";
import { EBallotBoxSealPolicy, EVotingStatus } from "@sequentech/ui-core";
import { BallotHashCopyButton, theme } from "@sequentech/ui-essentials";
import CardChart from "../charts/Charts";
import { outlinedWarningChipSx, wrappingChipSx } from "./ballotBoxChip";
import { SettingsContext } from "@/providers/SettingsContextProvider";
import { useEventPresentation } from "@/hooks/useZonedFormat";
import { intlLanguage, useZonedTime } from "@/hooks/useZonedTime";
import { formatList, shortHash } from "@/components/signing/format";
import { GET_BALLOT_BOX_AREA_NAMES, GET_BALLOT_BOX_AREAS, GET_BALLOT_BOX_SEALS, } from "@/queries/GetBallotBoxSeals";
import { sealText } from "@/services/ballotBoxSealErrors";
import { SealRecordLink } from "./SealRecordLink";
import { electionSealChannels, sealProgress } from "@/services/sealOnStop";
import { EBallotBoxClosedByKind, EBallotBoxSealStatus, EBallotBoxWaitingReason, parseWaitingReason, } from "@/types/ballotBoxSeal";
/** The categories of a failed attempt the sealer records (`error:<category>`). */
export var WAITING_ERROR_CATEGORIES = [
    "board",
    "census",
    "keystore",
    "storage",
    "settings",
    "ballots",
    "database",
    "other",
];
/** What the card shows for one ballot box. */
export var EBallotBoxRowStatus;
(function (EBallotBoxRowStatus) {
    /** No seal row: voting has not closed. */
    EBallotBoxRowStatus["OPEN"] = "open";
    /** Closed; sealed when the grace period ends. */
    EBallotBoxRowStatus["SEALING"] = "sealing";
    /** Past its deadline and the sealer is on it (it takes up to a minute). */
    EBallotBoxRowStatus["DUE"] = "due";
    /**
     * Past its deadline and held: a channel, Datafix votes, ballots the sequencer hasn't
     * added to the log, an error, or no sealer running.
     */
    EBallotBoxRowStatus["OVERDUE"] = "overdue";
    /** Sealed, locked and on the bulletin board; its seal record is being published. */
    EBallotBoxRowStatus["PUBLISHING"] = "publishing";
    /** Sealed, and its seal record is published. */
    EBallotBoxRowStatus["SEALED"] = "sealed";
    /** Not sealed: an incident. The ballot box stays locked. */
    EBallotBoxRowStatus["FAILED"] = "failed";
})(EBallotBoxRowStatus || (EBallotBoxRowStatus = {}));
/**
 * How long after its deadline, or after the sealer's last attempt, a pending
 * box counts as overdue: the beat tries every minute.
 */
export var STALE_ATTEMPT_MS = 3 * 60000;
/** Whether a pending box's last attempt (or its deadline, if never tried) is stale. */
var isStale = function (seal, now) {
    var _a;
    return now.getTime() - new Date((_a = seal.last_attempt_at) !== null && _a !== void 0 ? _a : seal.grace_deadline).getTime() >
        STALE_ATTEMPT_MS;
};
export var ballotBoxRowStatus = function (seal, now) {
    var _a;
    if (!seal)
        return EBallotBoxRowStatus.OPEN;
    switch (seal.status) {
        case EBallotBoxSealStatus.PENDING: {
            if (now.getTime() < new Date(seal.grace_deadline).getTime()) {
                return EBallotBoxRowStatus.SEALING;
            }
            var waiting = (_a = parseWaitingReason(seal.waiting_reason)) === null || _a === void 0 ? void 0 : _a.reason;
            var held = waiting === EBallotBoxWaitingReason.CHANNEL_OPEN ||
                waiting === EBallotBoxWaitingReason.CHANNEL_NOT_ENABLED ||
                waiting === EBallotBoxWaitingReason.CHANNEL_HAS_BALLOTS ||
                waiting === EBallotBoxWaitingReason.DATAFIX_VOTES ||
                waiting === EBallotBoxWaitingReason.SEQUENCER ||
                waiting === EBallotBoxWaitingReason.ERROR;
            return held || isStale(seal, now)
                ? EBallotBoxRowStatus.OVERDUE
                : EBallotBoxRowStatus.DUE;
        }
        case EBallotBoxSealStatus.SEALED:
            return EBallotBoxRowStatus.PUBLISHING;
        case EBallotBoxSealStatus.PUBLISHED:
            return EBallotBoxRowStatus.SEALED;
        default:
            return EBallotBoxRowStatus.FAILED;
    }
};
var STATUS_CHIP = (_a = {},
    _a[EBallotBoxRowStatus.OPEN] = { color: "default", variant: "outlined" },
    _a[EBallotBoxRowStatus.SEALING] = { color: "default", variant: "outlined" },
    _a[EBallotBoxRowStatus.DUE] = { color: "default", variant: "outlined" },
    _a[EBallotBoxRowStatus.OVERDUE] = { color: "warning", variant: "outlined" },
    _a[EBallotBoxRowStatus.PUBLISHING] = { color: "warning", variant: "outlined" },
    _a[EBallotBoxRowStatus.SEALED] = { color: "success", variant: "filled" },
    _a[EBallotBoxRowStatus.FAILED] = { color: "error", variant: "filled" },
    _a);
/** The latest of some ISO times. */
var latest = function (values) {
    return values
        .filter(function (value) { return !!value; })
        .reduce(function (max, value) { return (!max || new Date(value) > new Date(max) ? value : max); }, undefined);
};
/**
 * Ballot boxes (VOTE-FREEZE): one row per ballot box of the election with its seal,
 * shown on the election's Dashboard when the event seals its ballot boxes at
 * close. Nothing is shown when it doesn't.
 */
export var BallotBoxesCard = function (_a) {
    var electionEventId = _a.electionEventId, electionId = _a.electionId, election = _a.election, now = _a.now;
    var presentation = useEventPresentation(electionEventId);
    var sealAtClose = (presentation === null || presentation === void 0 ? void 0 : presentation.ballot_box_seal_policy) === EBallotBoxSealPolicy.SEAL_AT_CLOSE;
    if (!sealAtClose || !electionEventId || !electionId)
        return null;
    return (_jsx(BallotBoxesTable, { electionEventId: electionEventId, electionId: electionId, election: election, now: now }));
};
/** How often the card's clock ticks while a ballot box is pending. */
var PENDING_TICK_MS = 30000;
var BallotBoxesTable = function (_a) {
    var electionEventId = _a.electionEventId, electionId = _a.electionId, election = _a.election, fixedNow = _a.now;
    var _b = useTranslation(), t = _b.t, i18n = _b.i18n;
    var globalSettings = useContext(SettingsContext).globalSettings;
    var pollInterval = globalSettings.QUERY_POLL_INTERVAL_MS;
    var _c = useState(function () { return new Date(); }), clock = _c[0], setClock = _c[1];
    var now = fixedNow !== null && fixedNow !== void 0 ? fixedNow : clock;
    var zonedTime = useZonedTime(now);
    var _d = useQuery(GET_BALLOT_BOX_SEALS, { variables: { electionEventId: electionEventId, electionIds: [electionId] }, pollInterval: pollInterval }), sealsData = _d.data, sealsError = _d.error;
    var seals = useMemo(function () { var _a; return (_a = sealsData === null || sealsData === void 0 ? void 0 : sealsData.sequent_backend_ballot_box_seal) !== null && _a !== void 0 ? _a : []; }, [sealsData === null || sealsData === void 0 ? void 0 : sealsData.sequent_backend_ballot_box_seal]);
    // Polling doesn't re-render an unchanged pending row, so the clock ticks
    // while one waits for the end of its grace period.
    var anyPending = seals.some(function (seal) { return seal.status === EBallotBoxSealStatus.PENDING; });
    useEffect(function () {
        if (fixedNow || !anyPending)
            return;
        setClock(new Date());
        var timer = window.setInterval(function () { return setClock(new Date()); }, PENDING_TICK_MS);
        return function () { return window.clearInterval(timer); };
    }, [fixedNow, anyPending]);
    // The election's ballot boxes before the close: the areas of its published
    // ballot styles, the set the close seals. Not reading them only drops the
    // Open rows; the seal rows are listed anyway.
    var stylesData = useQuery(GET_BALLOT_BOX_AREAS, { variables: { electionEventId: electionEventId, electionId: electionId } }).data;
    var areaIds = useMemo(function () {
        var _a;
        return Array.from(new Set(((_a = stylesData === null || stylesData === void 0 ? void 0 : stylesData.sequent_backend_ballot_style) !== null && _a !== void 0 ? _a : [])
            .map(function (style) { return style.area_id; })
            .filter(function (id) { return !!id; }))).sort();
    }, [stylesData]);
    var namesData = useQuery(GET_BALLOT_BOX_AREA_NAMES, { variables: { areaIds: areaIds }, skip: !areaIds.length }).data;
    // One row per ballot box: every area of the election, merged with the seal
    // rows the close inserts (one per ballot box it seals).
    var rows = useMemo(function () {
        var _a;
        var names = new Map(((_a = namesData === null || namesData === void 0 ? void 0 : namesData.sequent_backend_area) !== null && _a !== void 0 ? _a : []).map(function (area) {
            var _a;
            return [
                String(area.id),
                (_a = area.name) !== null && _a !== void 0 ? _a : String(area.id),
            ];
        }));
        var ids = new Set(__spreadArray(__spreadArray([], areaIds, true), seals.map(function (seal) { return seal.area_id; }), true));
        return Array.from(ids, function (areaId) {
            var _a, _b, _c, _d;
            var seal = seals.find(function (row) { return row.area_id === areaId; });
            return {
                areaId: areaId,
                areaName: (_d = (_c = (_a = seal === null || seal === void 0 ? void 0 : seal.area_name) !== null && _a !== void 0 ? _a : (_b = seal === null || seal === void 0 ? void 0 : seal.area) === null || _b === void 0 ? void 0 : _b.name) !== null && _c !== void 0 ? _c : names.get(areaId)) !== null && _d !== void 0 ? _d : areaId,
                seal: seal,
            };
        }).sort(function (a, b) { return a.areaName.localeCompare(b.areaName, i18n.language); });
    }, [areaIds, namesData, seals, i18n.language]);
    var closed = latest(seals.map(function (seal) { return seal.closed_at; }));
    var deadline = latest(seals.map(function (seal) { return seal.grace_deadline; }));
    var closeRow = seals.find(function (seal) { return seal.closed_at === closed; });
    var channelNames = function (channels) {
        return new Intl.ListFormat(intlLanguage(i18n.language), {
            style: "long",
            type: "conjunction",
        }).format(channels.map(function (channel) { return t("publish.dialog.channel.".concat(channel)); }));
    };
    /** What the card says before any ballot box has a seal row, from the election's status. */
    var beforeClose = function () {
        if (!election)
            return t("dashboard.ballotBoxes.beforeClose");
        var all = electionSealChannels(election);
        var channels = all.filter(function (_a) {
            var enabled = _a.enabled;
            return enabled;
        });
        var progress = sealProgress(all);
        // A channel the Post doesn't enable that is open or ran holds the
        // seal: say so, whatever the enabled channels' state.
        var notEnabled = progress.notEnabled
            .map(function (channel) {
            return t("publish.dialog.sealNotEnabled", {
                channel: t("publish.dialog.channel.".concat(channel)),
            });
        })
            .join(" ");
        var closed = channels.some(function (_a) {
            var status = _a.status;
            return status === EVotingStatus.CLOSED;
        });
        if (closed && progress.holding.length) {
            return [
                t("dashboard.ballotBoxes.holding", {
                    count: progress.holding.length,
                    channels: channelNames(progress.holding),
                }),
                notEnabled,
            ]
                .filter(Boolean)
                .join(" ");
        }
        if (notEnabled)
            return notEnabled;
        var open = channels.filter(function (_a) {
            var status = _a.status;
            return status === EVotingStatus.OPEN;
        });
        if (open.length) {
            return t("dashboard.ballotBoxes.openOn", {
                channels: channelNames(open.map(function (_a) {
                    var channel = _a.channel;
                    return channel;
                })),
            });
        }
        if (channels.some(function (_a) {
            var status = _a.status;
            return status === EVotingStatus.PAUSED;
        })) {
            return t("dashboard.ballotBoxes.paused");
        }
        if (channels.every(function (_a) {
            var status = _a.status;
            return status === EVotingStatus.NOT_STARTED;
        })) {
            return t("dashboard.ballotBoxes.notStarted");
        }
        return t("dashboard.ballotBoxes.beforeClose");
    };
    var pending = seals.some(function (seal) { return seal.status === EBallotBoxSealStatus.PENDING; });
    var summary = sealsError
        ? t("dashboard.ballotBoxes.loadError")
        : !seals.length
            ? beforeClose()
            : seals.some(function (seal) { return seal.status === EBallotBoxSealStatus.FAILED; })
                ? t("dashboard.ballotBoxes.failed", { closed: zonedTime(closed) })
                : !pending
                    ? t("dashboard.ballotBoxes.sealed", { closed: zonedTime(closed) })
                    : deadline === closed
                        ? t("dashboard.ballotBoxes.sealingNow", { closed: zonedTime(closed) })
                        : deadline && now.getTime() < new Date(deadline).getTime()
                            ? t("dashboard.ballotBoxes.sealing", {
                                closed: zonedTime(closed),
                                deadline: zonedTime(deadline),
                            })
                            : t("dashboard.ballotBoxes.sealingPastGrace", {
                                closed: zonedTime(closed),
                                deadline: zonedTime(deadline),
                            });
    var closedByText = function (closedBy) {
        var _a, _b;
        switch (closedBy === null || closedBy === void 0 ? void 0 : closedBy.kind) {
            case EBallotBoxClosedByKind.SIGNED: {
                var names = ((_a = closedBy.signers) !== null && _a !== void 0 ? _a : []).map(function (signer) { return signer.name; });
                if (!names.length)
                    return null;
                return t("dashboard.ballotBoxes.closedBySignatures", {
                    names: formatList(names, intlLanguage(i18n.language)),
                    code: (_b = closedBy.signing_code) !== null && _b !== void 0 ? _b : "",
                });
            }
            case EBallotBoxClosedByKind.USER:
                return closedBy.username
                    ? t("dashboard.ballotBoxes.closedByUser", { username: closedBy.username })
                    : null;
            case EBallotBoxClosedByKind.SCHEDULED:
                return t("dashboard.ballotBoxes.closedBySchedule");
            default:
                return null;
        }
    };
    var closedBy = closedByText(closeRow === null || closeRow === void 0 ? void 0 : closeRow.closed_by);
    var statusLabel = function (status, seal) {
        return status === EBallotBoxRowStatus.SEALING
            ? t("dashboard.ballotBoxes.status.sealing", { time: zonedTime(seal === null || seal === void 0 ? void 0 : seal.grace_deadline) })
            : t("dashboard.ballotBoxes.status.".concat(status));
    };
    /** Why a ballot box is in its state, shown under its status (not only on hover). */
    var statusHelp = function (status, seal) {
        var _a;
        if (!seal)
            return "";
        switch (status) {
            case EBallotBoxRowStatus.PUBLISHING:
                return t("dashboard.ballotBoxes.help.publishing");
            case EBallotBoxRowStatus.FAILED:
                return sealText(t, seal.failure_reason);
            case EBallotBoxRowStatus.DUE:
                return t("dashboard.ballotBoxes.why.due");
            case EBallotBoxRowStatus.OVERDUE: {
                var waiting = parseWaitingReason(seal.waiting_reason);
                if ((waiting === null || waiting === void 0 ? void 0 : waiting.reason) === EBallotBoxWaitingReason.CHANNEL_OPEN && waiting.detail) {
                    return t("dashboard.ballotBoxes.why.channelOpen", {
                        channel: t("publish.dialog.channel.".concat(waiting.detail)),
                    });
                }
                if ((waiting === null || waiting === void 0 ? void 0 : waiting.reason) === EBallotBoxWaitingReason.CHANNEL_NOT_ENABLED &&
                    waiting.detail) {
                    return t("dashboard.ballotBoxes.why.channelNotEnabled", {
                        channel: t("publish.dialog.channel.".concat(waiting.detail)),
                    });
                }
                if ((waiting === null || waiting === void 0 ? void 0 : waiting.reason) === EBallotBoxWaitingReason.CHANNEL_HAS_BALLOTS &&
                    waiting.detail) {
                    return t("dashboard.ballotBoxes.why.channelHasBallots", {
                        channel: t("publish.dialog.channel.".concat(waiting.detail)),
                    });
                }
                if ((waiting === null || waiting === void 0 ? void 0 : waiting.reason) === EBallotBoxWaitingReason.DATAFIX_VOTES) {
                    return t("dashboard.ballotBoxes.why.datafixVotes", {
                        count: Number(waiting.detail) || 0,
                    });
                }
                if ((waiting === null || waiting === void 0 ? void 0 : waiting.reason) === EBallotBoxWaitingReason.SEQUENCER) {
                    return t("dashboard.ballotBoxes.why.sequencer", {
                        count: Number(waiting.detail) || 0,
                    });
                }
                if ((waiting === null || waiting === void 0 ? void 0 : waiting.reason) === EBallotBoxWaitingReason.ERROR) {
                    // A category code, never the error's own text (it may hold
                    // hosts or URLs); the service log has the details.
                    return WAITING_ERROR_CATEGORIES.includes((_a = waiting.detail) !== null && _a !== void 0 ? _a : "")
                        ? t("dashboard.ballotBoxes.why.errorCategory.".concat(waiting.detail))
                        : t("dashboard.ballotBoxes.why.errorCategory.other");
                }
                return seal.last_attempt_at
                    ? t("dashboard.ballotBoxes.why.stale", { time: zonedTime(seal.last_attempt_at) })
                    : t("dashboard.ballotBoxes.why.notTried");
            }
            default:
                return "";
        }
    };
    var copyLabels = {
        copy: t("dashboard.ballotBoxes.copyHash"),
        copied: t("dashboard.ballotBoxes.copied"),
        error: t("dashboard.ballotBoxes.copyError"),
    };
    var number = function (value) {
        return value === null || value === undefined ? "—" : value.toLocaleString(i18n.language);
    };
    return (_jsx(Box, { sx: { maxWidth: 1024, marginX: "auto", marginTop: 2 }, children: _jsx(CardChart, { title: t("dashboard.ballotBoxes.title"), children: sealsError ? (
            // Unknown, not "Open": the seals could not be read.
            _jsx(Alert, { severity: "warning", sx: { mt: 1 }, children: summary })) : (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "body2", sx: { mt: 1 }, children: summary }), closedBy ? (_jsx(Typography, { variant: "body2", color: theme.palette.customGrey.main, children: closedBy })) : null, rows.length ? (_jsx(Box, { sx: { overflowX: "auto", position: "relative", mt: 1 }, children: _jsxs(Table, { size: "small", sx: { "& th": { whiteSpace: "nowrap" } }, "aria-label": t("dashboard.ballotBoxes.title"), children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { children: t("dashboard.ballotBoxes.column.area") }), _jsx(TableCell, { children: t("dashboard.ballotBoxes.column.status") }), _jsx(TableCell, { align: "right", children: t("dashboard.ballotBoxes.column.inTheBox") }), _jsx(TableCell, { align: "right", children: t("dashboard.ballotBoxes.column.counted") }), _jsx(TableCell, { children: t("dashboard.ballotBoxes.column.sealedAt") }), _jsx(TableCell, { children: t("dashboard.ballotBoxes.column.sealHash") }), _jsx(TableCell, { children: t("dashboard.ballotBoxes.column.record") })] }) }), _jsx(TableBody, { children: rows.map(function (_a) {
                                        var areaId = _a.areaId, areaName = _a.areaName, seal = _a.seal;
                                        var status = ballotBoxRowStatus(seal, now);
                                        var chip = STATUS_CHIP[status];
                                        var help = statusHelp(status, seal);
                                        var isSealed = status === EBallotBoxRowStatus.SEALED ||
                                            status === EBallotBoxRowStatus.PUBLISHING;
                                        return (_jsxs(TableRow, { children: [_jsx(TableCell, { children: areaName }), _jsxs(TableCell, { sx: { minWidth: 190 }, children: [_jsx(Chip, { size: "small", label: statusLabel(status, seal), color: chip.color, variant: chip.variant, sx: [
                                                                wrappingChipSx,
                                                                chip.color === "warning" &&
                                                                    chip.variant === "outlined" &&
                                                                    outlinedWarningChipSx,
                                                            ] }), help ? (_jsx(Typography, { variant: "caption", display: "block", sx: { maxWidth: 260, mt: 0.5 }, color: status ===
                                                                EBallotBoxRowStatus.FAILED
                                                                ? "error"
                                                                : "text.secondary", children: help })) : null] }), _jsx(TableCell, { align: "right", children: isSealed
                                                        ? number(seal === null || seal === void 0 ? void 0 : seal.ballots_in_box)
                                                        : "—" }), _jsx(TableCell, { align: "right", children: isSealed
                                                        ? number(seal === null || seal === void 0 ? void 0 : seal.ballots_counted)
                                                        : "—" }), _jsx(TableCell, { sx: { whiteSpace: "nowrap" }, children: isSealed && (seal === null || seal === void 0 ? void 0 : seal.sealed_at)
                                                        ? zonedTime(seal.sealed_at)
                                                        : "—" }), _jsx(TableCell, { sx: { whiteSpace: "nowrap" }, children: isSealed && (seal === null || seal === void 0 ? void 0 : seal.seal_hash) ? (_jsxs(Box, { component: "span", sx: {
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                        }, children: [_jsx(Box, { component: "span", sx: {
                                                                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                                                                }, title: seal.seal_hash, children: shortHash(seal.seal_hash) }), _jsx(BallotHashCopyButton, { hash: seal.seal_hash, copyLabels: copyLabels })] })) : ("—") }), _jsx(TableCell, { sx: { whiteSpace: "nowrap" }, children: status === EBallotBoxRowStatus.SEALED &&
                                                        seal ? (_jsx(SealRecordLink, { electionEventId: electionEventId, seal: seal, areaName: areaName })) : status ===
                                                        EBallotBoxRowStatus.PUBLISHING ? (t("dashboard.ballotBoxes.notYet")) : ("—") })] }, areaId));
                                    }) })] }) })) : null, rows.some(function (_a) {
                        var seal = _a.seal;
                        return (seal === null || seal === void 0 ? void 0 : seal.ballots_counted) !== undefined &&
                            (seal === null || seal === void 0 ? void 0 : seal.ballots_counted) !== null;
                    }) ? (_jsx(Typography, { variant: "caption", display: "block", color: "text.secondary", sx: { mt: 1 }, children: t("dashboard.ballotBoxes.help.counted") })) : null] })) }) }));
};
export default BallotBoxesCard;
